import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as timeEntriesApi from '../services/timeEntriesApi';
import type {
  TimeEntryFilters,
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
  CreateInvoiceFromEntriesRequest,
} from '../types/timeEntry';

// Query keys
export const timeEntryKeys = {
  all: ['timeEntries'] as const,
  lists: () => [...timeEntryKeys.all, 'list'] as const,
  list: (filters: TimeEntryFilters) => [...timeEntryKeys.lists(), filters] as const,
  details: () => [...timeEntryKeys.all, 'detail'] as const,
  detail: (id: string) => [...timeEntryKeys.details(), id] as const,
  stats: (params?: { startDate?: string; endDate?: string; clientId?: string }) =>
    [...timeEntryKeys.all, 'stats', params] as const,
  running: () => [...timeEntryKeys.all, 'running'] as const,
  uninvoiced: () => [...timeEntryKeys.all, 'uninvoiced'] as const,
};

/**
 * Hook to fetch time entries with filters
 */
export const useTimeEntries = (filters?: TimeEntryFilters) => {
  return useQuery({
    queryKey: timeEntryKeys.list(filters || {}),
    queryFn: () => timeEntriesApi.getTimeEntries(filters),
  });
};

/**
 * Hook to fetch time entry statistics
 */
export const useTimeEntryStats = (params?: {
  startDate?: string;
  endDate?: string;
  clientId?: string;
}) => {
  return useQuery({
    queryKey: timeEntryKeys.stats(params),
    queryFn: () => timeEntriesApi.getTimeEntryStats(params),
  });
};

/**
 * Hook to fetch currently running timer
 * Only polls when the browser tab is visible to prevent unnecessary API calls
 * and session expiration issues when the user is away
 */
export const useRunningTimer = () => {
  return useQuery({
    queryKey: timeEntryKeys.running(),
    queryFn: timeEntriesApi.getRunningTimer,
    refetchInterval: 1000, // Refresh every second for live timer
    refetchIntervalInBackground: false, // Stop polling when tab is not visible
  });
};

/**
 * Hook to fetch a single time entry
 */
export const useTimeEntry = (id: string) => {
  return useQuery({
    queryKey: timeEntryKeys.detail(id),
    queryFn: () => timeEntriesApi.getTimeEntry(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch uninvoiced time entries by client
 */
export const useUninvoicedByClient = () => {
  return useQuery({
    queryKey: timeEntryKeys.uninvoiced(),
    queryFn: timeEntriesApi.getUninvoicedByClient,
  });
};

/**
 * Hook for time entry mutations
 */
export const useTimeEntryMutations = () => {
  const queryClient = useQueryClient();

  const invalidateTimeEntries = () => {
    queryClient.invalidateQueries({ queryKey: timeEntryKeys.all });
  };

  const createTimeEntry = useMutation({
    mutationFn: (data: CreateTimeEntryRequest) => timeEntriesApi.createTimeEntry(data),
    onSuccess: invalidateTimeEntries,
  });

  const updateTimeEntry = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryRequest }) =>
      timeEntriesApi.updateTimeEntry(id, data),
    onSuccess: invalidateTimeEntries,
  });

  const stopTimer = useMutation({
    mutationFn: (id: string) => timeEntriesApi.stopTimer(id),
    onSuccess: invalidateTimeEntries,
  });

  const pauseTimer = useMutation({
    mutationFn: (id: string) => timeEntriesApi.pauseTimer(id),
    onSuccess: invalidateTimeEntries,
  });

  const resumeTimer = useMutation({
    mutationFn: (id: string) => timeEntriesApi.resumeTimer(id),
    onSuccess: invalidateTimeEntries,
  });

  const deleteTimeEntry = useMutation({
    mutationFn: (id: string) => timeEntriesApi.deleteTimeEntry(id),
    onSuccess: invalidateTimeEntries,
  });

  const createInvoiceFromEntries = useMutation({
    mutationFn: (data: CreateInvoiceFromEntriesRequest) =>
      timeEntriesApi.createInvoiceFromEntries(data),
    onSuccess: invalidateTimeEntries,
  });

  return {
    createTimeEntry,
    updateTimeEntry,
    stopTimer,
    pauseTimer,
    resumeTimer,
    deleteTimeEntry,
    createInvoiceFromEntries,
  };
};
